import { format, parse, isAfter, differenceInMinutes, isSameDay } from "date-fns";
import { EmployeeShiftDisplayData } from "@/types/time-tracking";
// Removed createBusinessShiftStatusService - using unified service only
import { ShiftStatus } from '@/lib/scheduling/shift-status-types';

import { DailyAttendanceSummary, EmployeeAttendanceStatus, BusinessAttendanceMetrics } from "./attendance-service";
import { formatDurationAsHours } from "@/lib/core/time-formatting";

// Legacy functions removed - functionality moved to unified shift status system
// Use defaultShiftStatusService.calculateDetailedShiftStatus() instead of calculateArrivalStatus
// Use ShiftStatusBadge component instead of renderStatusBadge

/**
 * Formats a UTC Date object for display in the business timezone.
 *
 * IMPORTANT: This function expects UTC Date objects from the database/service layer.
 * The service layer (UnifiedShiftService) returns times as UTC - do NOT pre-convert times
 * before calling this function, or you will get double conversion issues.
 *
 * @param utcDate - Date object representing UTC time (from database/service)
 * @param businessTimezone - IANA timezone string (e.g., 'Europe/Bratislava')
 * @returns Formatted time string in HH:mm format (24-hour)
 */
function formatTimeInBusinessTimezone(utcDate: Date, businessTimezone: string = 'UTC'): string {
  try {
    return utcDate.toLocaleString('en-US', {
      timeZone: businessTimezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch (error) {
    console.warn(`Failed to format time in timezone ${businessTimezone}:`, error);
    return format(utcDate, 'HH:mm'); // Fallback to browser timezone
  }
}

export const processShiftsToDisplayData = (
  dayShifts: any[],
  employees: any[],
  businessData?: any,
  businessTimezone: string = 'UTC'
): EmployeeShiftDisplayData[] => {
  const combinedData: EmployeeShiftDisplayData[] = [];

  for (const shift of dayShifts) {
    const hasAssignedEmployee = shift.employeeId && shift.employeeName;

    if (hasAssignedEmployee) {
      const shiftStartTime = shift.startTime;
      const actualArrivalTimeDate = shift.actualArrivalTime ? new Date(shift.actualArrivalTime) : null;
      const actualDepartureTimeDate = shift.actualDepartureTime ? new Date(shift.actualDepartureTime) : null;

      // Use pre-calculated status from UnifiedShiftService - single source of truth
      const status = shift.calculatedStatus || ShiftStatus.PENDING;

      // For timing details, we can extract from the shift data if available
      // or calculate basic late/early minutes for display purposes only
      const lateMinutes = shift.lateMinutes || 0;
      const minutesEarly = shift.minutesEarly || 0;

      combinedData.push({
        id: `${shift.id}-${shift.employeeId}`,
        employeeName: shift.employeeName,
        employeeId: shift.employeeId,
        shiftId: shift.id,
        scheduledStartTime: formatTimeInBusinessTimezone(shiftStartTime, businessTimezone),
        scheduledEndTime: formatTimeInBusinessTimezone(shift.endTime, businessTimezone),
        actualArrivalTime: actualArrivalTimeDate ? formatTimeInBusinessTimezone(actualArrivalTimeDate, businessTimezone) : null,
        actualDepartureTime: actualDepartureTimeDate ? formatTimeInBusinessTimezone(actualDepartureTimeDate, businessTimezone) : null,
        status,
        lateMinutes,
        minutesEarly
      });
    } else {
      // For unassigned shifts, use UNASSIGNED status
      combinedData.push({
        id: shift.id,
        employeeName: "Unassigned",
        shiftId: shift.id,
        scheduledStartTime: formatTimeInBusinessTimezone(shift.startTime, businessTimezone),
        scheduledEndTime: formatTimeInBusinessTimezone(shift.endTime, businessTimezone),
        actualArrivalTime: null,
        actualDepartureTime: null,
        status: ShiftStatus.UNASSIGNED
      });
    }
  }

  return combinedData.sort((a, b) => {
    const timeA = parse(a.scheduledStartTime, 'HH:mm', new Date());
    const timeB = parse(b.scheduledStartTime, 'HH:mm', new Date());
    return timeA.getTime() - timeB.getTime();
  });
};

// ==================== ENHANCED SUMMARY CALCULATION FUNCTIONS ====================

/**
 * Calculate comprehensive time metrics from employee attendance statuses
 */
export const calculateTimeMetrics = (employeeStatuses: EmployeeAttendanceStatus[]) => {
  let totalWorkedMinutes = 0;
  let totalScheduledMinutes = 0;
  let employeesPresent = 0;
  let lateArrivals = 0;
  let earlyDepartures = 0;
  let overtimeMinutes = 0;

  employeeStatuses.forEach((employee) => {
    // Calculate scheduled hours
    const scheduledMinutes = employee.hoursScheduled * 60;
    totalScheduledMinutes += scheduledMinutes;

    // Track attendance metrics
    if (employee.actualArrivalTime) {
      employeesPresent++;
      if (employee.lateMinutes && employee.lateMinutes > 0) {
        lateArrivals++;
      }
    }

    // Track early departures (if departed more than 15 minutes before scheduled end)
    if (employee.actualDepartureTime && employee.actualArrivalTime) {
      const scheduledEndTime = employee.scheduledEndTime;
      const earlyThreshold = new Date(scheduledEndTime.getTime() - 15 * 60000);
      if (employee.actualDepartureTime < earlyThreshold) {
        earlyDepartures++;
      }
    }

    // Calculate worked hours and overtime
    totalWorkedMinutes += employee.hoursWorked * 60;
    overtimeMinutes += employee.overtimeMinutes;
  });

  const totalWorkedHours = totalWorkedMinutes / 60;
  const totalScheduledHours = totalScheduledMinutes / 60;
  const overtimeHours = overtimeMinutes / 60;
  
  const utilizationRate = totalScheduledHours > 0 
    ? Math.round((totalWorkedHours / totalScheduledHours) * 100) 
    : 0;
    
  const punctualityRate = employeeStatuses.length > 0
    ? Math.round(((employeeStatuses.length - lateArrivals) / employeeStatuses.length) * 100)
    : 100;

  return {
    totalWorkedHours: formatDurationAsHours(totalWorkedMinutes),
    totalScheduledHours: formatDurationAsHours(totalScheduledMinutes),
    overtimeHours: formatDurationAsHours(overtimeMinutes),
    utilizationRate,
    punctualityRate,
    employeesPresent,
    totalEmployees: employeeStatuses.length,
    lateArrivals,
    earlyDepartures,
    totalWorkedMinutes,
    totalScheduledMinutes,
    overtimeMinutes
  };
};

// Use formatDurationAsHours from core utilities

/**
 * Get color class for attendance status using unified ShiftStatus
 */
export const getAttendanceStatusColor = (status: ShiftStatus): string => {
  switch (status) {
    case ShiftStatus.ON_TIME:
    case ShiftStatus.COMPLETED:
      return 'text-green-500';
    case ShiftStatus.LATE:
    case ShiftStatus.COMPLETED_LATE:
      return 'text-orange-500';
    case ShiftStatus.TOO_EARLY:
      return 'text-primary';
    case ShiftStatus.MISSED:
    case ShiftStatus.NO_DEPARTURE_SCAN:
      return 'text-red-500';
    case ShiftStatus.PENDING:
      return 'text-blue-500';
    case ShiftStatus.UNASSIGNED:
      return 'text-gray-500';
    case ShiftStatus.IN_PROGRESS:
      return 'text-emerald-500';
    case ShiftStatus.LEFT_EARLY:
      return 'text-amber-500';
    default:
      return 'text-gray-400';
  }
};

/**
 * Calculate attendance summary statistics from daily data
 */
export const calculateAttendanceSummaryStats = (summary: DailyAttendanceSummary) => {
  const { totalScheduledEmployees, totalPresentEmployees, statusCounts } = summary;
  
  return {
    attendancePercentage: totalScheduledEmployees > 0 
      ? Math.round((totalPresentEmployees / totalScheduledEmployees) * 100) 
      : 100,
    punctualityPercentage: totalScheduledEmployees > 0
      ? Math.round(((statusCounts.get(ShiftStatus.ON_TIME) || 0) / totalScheduledEmployees) * 100)
      : 100,
    latePercentage: totalScheduledEmployees > 0
      ? Math.round(((statusCounts.get(ShiftStatus.LATE) || 0) / totalScheduledEmployees) * 100)
      : 0,
    noShowPercentage: totalScheduledEmployees > 0
      ? Math.round(((statusCounts.get(ShiftStatus.MISSED) || 0) / totalScheduledEmployees) * 100)
      : 0
  };
};

/**
 * Get business status indication based on attendance summary
 */
export const getBusinessStatusFromAttendance = (summary: DailyAttendanceSummary) => {
  const { totalScheduledEmployees, totalPresentEmployees, statusCounts } = summary;
  const hasIssues = (statusCounts.get(ShiftStatus.LATE) || 0) > 0 || (statusCounts.get(ShiftStatus.MISSED) || 0) > 0;
  
  if (totalPresentEmployees === totalScheduledEmployees && !hasIssues) {
    return { 
      text: 'Optimal', 
      color: 'bg-green-500',
      textColor: 'text-green-500',
      description: 'All employees present and on time'
    };
  } else if (totalPresentEmployees < totalScheduledEmployees || (statusCounts.get(ShiftStatus.MISSED) || 0) > 0) {
    return { 
      text: 'Understaffed', 
      color: 'bg-red-500',
      textColor: 'text-red-500',
      description: 'Missing employees for scheduled shifts'
    };
  } else if (hasIssues) {
    return { 
      text: 'Issues Present', 
      color: 'bg-orange-500',
      textColor: 'text-orange-500',
      description: 'Attendance or punctuality issues detected'
    };
  } else if (totalPresentEmployees > totalScheduledEmployees) {
    return { 
      text: 'Overstaffed', 
      color: 'bg-blue-500',
      textColor: 'text-blue-500',
      description: 'More employees present than scheduled'
    };
  } else {
    return { 
      text: 'Good', 
      color: 'bg-green-500',
      textColor: 'text-green-500',
      description: 'Acceptable attendance levels'
    };
  }
};

/**
 * Calculate calendar day status for unified calendar components
 */
export const calculateCalendarDayStatus = (
  dayShifts: any[], 
  timeLogs: any[],
  referenceDate: Date
): {
  status: 'green' | 'orange' | 'red' | 'blue' | 'gray';
  shiftsCount: number;
  timeLogsCount: number;
  coverage: number; // percentage
  issues: string[];
} => {
  const shiftsCount = dayShifts.length;
  const timeLogsCount = timeLogs.filter(log => 
    isSameDay(new Date(log.scanned_at), referenceDate)
  ).length;
  
  if (shiftsCount === 0) {
    return {
      status: 'gray',
      shiftsCount: 0,
      timeLogsCount: 0,
      coverage: 0,
      issues: ['No shifts scheduled']
    };
  }

  const assignedShifts = dayShifts.filter(shift => shift.employeeId);
  const unassignedShifts = dayShifts.filter(shift => !shift.employeeId);
  const employeesWithTimeLogs = new Set(
    timeLogs.filter(log => 
      log.event_type === 'arrival' && isSameDay(new Date(log.scanned_at), referenceDate)
    ).map(log => log.employee_id)
  );

  const coverage = assignedShifts.length > 0 
    ? Math.round((employeesWithTimeLogs.size / assignedShifts.length) * 100)
    : 0;

  const issues: string[] = [];
  
  if (unassignedShifts.length > 0) {
    issues.push(`${unassignedShifts.length} unassigned shift${unassignedShifts.length > 1 ? 's' : ''}`);
  }
  
  if (employeesWithTimeLogs.size < assignedShifts.length) {
    const missing = assignedShifts.length - employeesWithTimeLogs.size;
    issues.push(`${missing} employee${missing > 1 ? 's' : ''} not arrived`);
  }

  // Determine status
  if (coverage === 100 && unassignedShifts.length === 0) {
    return { status: 'green', shiftsCount, timeLogsCount, coverage, issues };
  } else if (coverage >= 80 && unassignedShifts.length <= 1) {
    return { status: 'orange', shiftsCount, timeLogsCount, coverage, issues };
  } else if (coverage >= 50) {
    return { status: 'red', shiftsCount, timeLogsCount, coverage, issues };
  } else if (timeLogsCount > 0) {
    return { status: 'blue', shiftsCount, timeLogsCount, coverage, issues };
  } else {
    return { status: 'gray', shiftsCount, timeLogsCount, coverage, issues };
  }
};

/**
 * Aggregate business metrics across multiple periods
 */
export const aggregateBusinessMetrics = (metrics: BusinessAttendanceMetrics[]) => {
  if (metrics.length === 0) return null;

  const totalEmployees = Math.max(...metrics.map(m => m.totalEmployees));
  const avgAttendanceRate = Math.round(
    metrics.reduce((sum, m) => sum + m.averageAttendanceRate, 0) / metrics.length
  );
  const avgPunctualityRate = Math.round(
    metrics.reduce((sum, m) => sum + m.averagePunctualityRate, 0) / metrics.length
  );
  const totalLateArrivals = metrics.reduce((sum, m) => sum + m.totalLateArrivals, 0);
  const totalEarlyDepartures = metrics.reduce((sum, m) => sum + m.totalEarlyDepartures, 0);
  const totalOvertimeHours = metrics.reduce((sum, m) => sum + m.totalOvertimeHours, 0);

  return {
    totalEmployees,
    avgAttendanceRate,
    avgPunctualityRate,
    totalLateArrivals,
    totalEarlyDepartures,
    totalOvertimeHours,
    periodsAnalyzed: metrics.length,
    periodRange: {
      start: new Date(Math.min(...metrics.map(m => m.period.start.getTime()))),
      end: new Date(Math.max(...metrics.map(m => m.period.end.getTime())))
    }
  };
};

/**
 * Calculate productivity metrics from attendance data
 */
export const calculateProductivityMetrics = (summary: DailyAttendanceSummary) => {
  const { timeMetrics, totalScheduledEmployees, totalPresentEmployees } = summary;
  
  const productivity = timeMetrics.totalScheduledMinutes > 0
    ? Math.round((timeMetrics.totalWorkedMinutes / timeMetrics.totalScheduledMinutes) * 100)
    : 0;

  const efficiency = totalScheduledEmployees > 0
    ? Math.round((totalPresentEmployees / totalScheduledEmployees) * 100)
    : 0;

  const overtimeRate = timeMetrics.totalWorkedMinutes > 0
    ? Math.round((timeMetrics.totalOvertimeMinutes / timeMetrics.totalWorkedMinutes) * 100)
    : 0;

  return {
    productivity, // How much of scheduled time was actually worked
    efficiency, // How many scheduled employees actually showed up
    overtimeRate, // Percentage of work time that was overtime
    utilizationRate: timeMetrics.utilizationRate,
    avgHoursPerEmployee: totalPresentEmployees > 0 
      ? Math.round((timeMetrics.totalWorkedMinutes / 60 / totalPresentEmployees) * 10) / 10
      : 0
  };
};

/**
 * Generate attendance insights and recommendations
 */
export const generateAttendanceInsights = (summary: DailyAttendanceSummary) => {
  const insights: string[] = [];
  const recommendations: string[] = [];
  
  const { statusCounts, attendanceRate, punctualityRate, timeMetrics } = summary;
  
  // Attendance insights
  if (attendanceRate < 80) {
    insights.push(`Low attendance rate: ${attendanceRate}%`);
    recommendations.push('Review scheduling practices and employee communication');
  }
  
  if (punctualityRate < 85) {
    insights.push(`Punctuality issues: ${statusCounts.get(ShiftStatus.LATE) || 0} late arrivals`);
    recommendations.push('Consider implementing punctuality incentives or flexible start times');
  }
  
  if ((statusCounts.get(ShiftStatus.TOO_EARLY) || 0) > 2) {
    insights.push(`${statusCounts.get(ShiftStatus.TOO_EARLY) || 0} employees arrived very early`);
    recommendations.push('Review shift start times or provide pre-shift activities');
  }
  
  if (timeMetrics.utilizationRate > 110) {
    insights.push(`High utilization: ${timeMetrics.utilizationRate}%`);
    recommendations.push('Monitor for burnout and consider additional staffing');
  }
  
  if (timeMetrics.totalOvertimeMinutes > 120) {
    insights.push(`Significant overtime: ${formatDurationAsHours(timeMetrics.totalOvertimeMinutes)}`);
    recommendations.push('Analyze workload distribution and staffing levels');
  }

  return { insights, recommendations };
};